import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { formatPhone, unformatPhone } from '../../util';
import {
  SignUpState,
  selectMentor,
  selectLicenseCount,
  selectSessionId,
  selectIsReturningMentor,
  setMentor,
} from '../../features/signUp/signUpSlice';
import { store, useAppSelector } from '../../stores/AppStore';
import { useUpdateSignupProfileMutation } from '../../services/apiSlice';
import { usCellPhonePattern } from '../../constants';
import ProgressBar from './ProgressBar';
import { FieldError } from '../ui/field-error';
import useSessionStatusCheck from '../../hooks/useSessionStatusCheck';

interface MentorDetailsProperties {
  onNextStep: () => void;
  onPreviousStep: () => void;
}

const MentorDetails = ({ onNextStep }: MentorDetailsProperties) => {
  const [emailError, setEmailError] = useState('');
  const mentor = useAppSelector(selectMentor);
  // const thinkers = useAppSelector(selectThinkers); // Not used in this component
  const licenseCount = useAppSelector(selectLicenseCount);
  const sessionId = useAppSelector(selectSessionId);
  const isReturningMentor = useAppSelector(selectIsReturningMentor);
  const { isValidating, validateAndProceed, validateOnMount } =
    useSessionStatusCheck();

  // Validate session status on mount
  useEffect(() => {
    validateOnMount();
  }, [validateOnMount]);
  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm<SignUpState['mentor']>({
    mode: 'all',
    defaultValues: mentor,
  });
  // const [
  //   checkEmailExists,
  //   { error: isCheckingEmailError, isFetching: isCheckingEmailExists },
  // ] = useLazyCheckEmailExistsQuery(); // Email is now read-only, no need to check
  const [updateSignupProfile, { isLoading: isUpdatingProfile }] =
    useUpdateSignupProfileMutation();

  const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(event.target.value);
    setValue('phone', formatted);
  };

  const handleContinue = async (data: SignUpState['mentor']) => {
    const phone = getValues('phone');
    if (phone) {
      setValue('phone', unformatPhone(phone));
      if (getValues('phone').length !== 10) {
        setError('phone', {
          message: 'Please enter a valid US cell phone number.',
        });
        return;
      }
    }

    // Note: Email is read-only, so we don't validate it here
    // Email validation was done during payment

    // Validate session status before proceeding
    validateAndProceed(async () => {
      // Update profile via API
      if (sessionId && mentor.updateToken) {
        try {
          await updateSignupProfile({
            sessionId,
            updateToken: mentor.updateToken,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phone: data.phone || undefined,
          }).unwrap();

          // Update Redux with new data
          store.dispatch(setMentor(getValues()));
          onNextStep();
        } catch (error: any) {
          console.error('Failed to update profile:', error);
          switch (error.status) {
            case 403: {
              setEmailError('Update window expired. Please contact support.');

              break;
            }
            case 409: {
              setEmailError('Email already exists.');

              break;
            }
            case 400: {
              setEmailError(
                error.data?.message ||
                  'Invalid data. Please check your inputs.',
              );

              break;
            }
            default: {
              setEmailError('Failed to update profile. Please try again.');
            }
          }
        }
      } else {
        // Fallback: just update Redux if no token (shouldn't happen in normal flow)
        console.warn('No update token available, using fallback');
        store.dispatch(setMentor(getValues()));
        onNextStep();
      }
    });
  };

  const cantProceed =
    !isValid || !!emailError || isUpdatingProfile || isValidating;

  return (
    <form
      data-testid="mentor-details"
      onSubmit={handleSubmit(handleContinue)}
      className="flex flex-col gap-2 md:gap-4 w-full h-full"
    >
      <div className="flex flex-col gap-2 md:gap-4 w-full md:w-[70%] lg:w-[60%] mx-auto">
        <h1 className="text-3xl md:text-4xl font-medium w-full">
          {isReturningMentor
            ? `Welcome back, ${mentor.firstName}!`
            : 'Mentor Details'}
        </h1>
        <p className="text-gray-500 text-sm md:text-base">
          {isReturningMentor
            ? `Great to see you again! You can review your details below before adding your new ${
                licenseCount === 1 ? 'Thinker' : 'Thinkers'
              }.`
            : `Please review and update your details if needed. You can change any information before continuing to set up your ${
                licenseCount === 1 ? 'Thinker' : 'Thinkers'
              }.`}
        </p>
        <p className="text-gray-500 text-sm md:text-base font-bold">
          {isReturningMentor
            ? 'A confirmation email has been sent to your email address.'
            : 'A welcome email with login instructions has been sent to your email address.'}
        </p>
        <div className="flex flex-col gap-3 md:gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6 items-start">
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="firstName"
                className="text-sm md:text-base font-medium text-gray-700"
              >
                First Name
              </label>
              <Controller
                render={({ field }) => (
                  <input
                    {...field}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full"
                    placeholder="Enter First Name"
                    id="firstName"
                  />
                )}
                name={`firstName`}
                control={control}
                rules={{ required: true, minLength: 2, maxLength: 50 }}
              />
              <FieldError
                errors={errors}
                field="firstName"
                fieldName="First name"
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="lastName"
                className="text-sm md:text-base font-medium text-gray-700"
              >
                Last Name
              </label>
              <Controller
                render={({ field }) => (
                  <input
                    {...field}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full"
                    placeholder="Enter Last Name"
                    id="lastName"
                  />
                )}
                name={`lastName`}
                control={control}
                rules={{ required: true, minLength: 2, maxLength: 50 }}
              />
              <FieldError
                errors={errors}
                field="lastName"
                fieldName="Last name"
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="email"
                className="text-sm md:text-base font-medium text-gray-700"
              >
                Email
              </label>
              <Controller
                render={({ field }) => (
                  <input
                    {...field}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full bg-gray-50 cursor-not-allowed"
                    placeholder="Enter Email"
                    id="email"
                    readOnly
                    disabled
                    title="If you want to change your email, please contact help@thinktankprep.com"
                  />
                )}
                name="email"
                control={control}
                rules={{}}
              />
              <p className="text-sm text-gray-500">
                If you want to change your email, please contact{' '}
                <a href="mailto:help@thinktankprep.com">
                  help@thinktankprep.com
                </a>
              </p>
              <FieldError errors={errors} field="email" fieldName="Email" />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <label
                htmlFor="phone"
                className="text-sm md:text-base font-medium text-gray-700"
              >
                Phone Number
                <span className="text-sm text-blue-400 italic pl-2">
                  optional
                </span>
              </label>
              <Controller
                render={({ field }) => (
                  <input
                    {...field}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full"
                    placeholder="Enter your phone number"
                    id="phone"
                    onChange={handlePhoneNumberChange}
                    value={
                      field.value.length > 0 ? formatPhone(field.value) : ''
                    }
                  />
                )}
                name="phone"
                control={control}
                rules={{ required: false, pattern: usCellPhonePattern }}
              />
              <FieldError
                errors={errors}
                field="phone"
                fieldName="Cell phone"
              />
              <p className="text-sm text-gray-500">
                By entering your phone number you agree to receive SMS updates
                regarding your account, your student
                {licenseCount > 1 ? "s'" : "'s"} account
                {licenseCount > 1 ? 's' : ''} and program related promotions.
                Data rates may apply.
              </p>
            </div>
          </div>
        </div>
      </div>

      <ProgressBar />

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4 md:pt-6 w-full justify-between items-center">
        <button
          data-testid={`back-button`}
          type="button"
          className="
              px-10 py-2 w-max rounded-full border font-medium
              transition-all duration-300 ease-in-out
              hover:shadow hover:outline hover:outline-tttDefault hover:outline-[1px]
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          disabled={true}
          title="Cannot go back after payment confirmation"
        >
          Back
        </button>
        <button
          data-testid={`continue-button`}
          type="submit"
          disabled={cantProceed}
          className="
            px-10 py-2 w-max rounded-full
            font-medium text-white bg-tttDefault
            transition-all duration-300 ease-in-out
            hover:shadow hover:bg-opacity-90
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default MentorDetails;
